// Financial Reporting suite — the ADMIN series catalog: every series row incl.
// Dormant, with activation-state flips (the phase-2 switch). Dormant series
// appear ONLY here — everywhere else they are absent, not greyed.
//
// The catalog is NOT a fixed 13: the FR seed contributes 13 rows, DW-M1 and
// VG-M1 are seeded separately, and retirements move rows between states. The
// header count is therefore DERIVED from `rows` — never write a literal here,
// it has already drifted once (the copy said "All 13 series" while the table
// rendered 15).

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import {
  Badge,
  Button,
  Card,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import type {
  ReportSeriesActivationState,
  ReportSeriesDto,
} from "@/api/portfolioAdmin/financialReportingApi";
import { useUpdateSeriesActivation } from "@/hooks/portfolioAdmin/useFinancialReporting";

const STATE_TONE: Record<ReportSeriesActivationState, "ok" | "progress" | "muted"> = {
  Active: "ok",
  TriggerArmed: "progress",
  Dormant: "muted",
};

const STATE_LABEL: Record<ReportSeriesActivationState, string> = {
  Active: "Active",
  TriggerArmed: "Trigger-armed",
  Dormant: "Dormant",
};

const ALL_STATES: ReportSeriesActivationState[] = ["Active", "TriggerArmed", "Dormant"];

interface PendingFlip {
  series: ReportSeriesDto;
  next: ReportSeriesActivationState;
}

export function SeriesCatalogTable({ rows }: { rows: ReportSeriesDto[] }) {
  const toast = useToast();
  const update = useUpdateSeriesActivation();
  const [pending, setPending] = useState<PendingFlip | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const activeCount = rows.filter((s) => s.activationState === "Active").length;

  const confirm = async () => {
    if (!pending) return;
    setError(null);
    try {
      await update.mutateAsync({
        code: pending.series.code,
        body: {
          activationState: pending.next,
          reason: reason.trim() === "" ? null : reason.trim(),
        },
      });
      toast.success(`${pending.series.code} is now ${STATE_LABEL[pending.next]}.`);
      setPending(null);
      setReason("");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Card data-testid="series-catalog">
      <h3 className="text-body font-semibold m-0">Series catalog</h3>
      <p className="text-caption text-gray-mid m-0 mt-space-1 mb-space-2">
        {/* Derived, never a literal — "series" is its own plural. */}
        {`${rows.length} series, ${activeCount} active. `}
        Activation is configuration — flipping a series to Active turns on its
        reminders and "Create report" affordance everywhere.
      </p>
      <Table>
        <THead>
          <TR>
            <TH>Code</TH>
            <TH>Title</TH>
            <TH>Cadence</TH>
            <TH>Level</TH>
            <TH>Deadline</TH>
            <TH>Signers</TH>
            <TH>Template</TH>
            <TH>State</TH>
            <TH aria-label="Actions" />
          </TR>
        </THead>
        <TBody>
          {rows.map((s) => (
            <TR key={s.code} data-testid={`catalog-row-${s.code}`}>
              <TD className="font-medium">{s.code}</TD>
              <TD>{s.title}</TD>
              <TD>{s.cadence}</TD>
              <TD>{s.level}</TD>
              <TD>{s.deadlineRule}</TD>
              <TD>
                {s.signerFlow === "PreparerReviewer"
                  ? "Preparer + Reviewer"
                  : "Authorized Officer"}
              </TD>
              <TD>{s.hasTemplate ? <Badge status="finalized">Live</Badge> : "—"}</TD>
              <TD>
                <StatusBadge tone={STATE_TONE[s.activationState]}>
                  {STATE_LABEL[s.activationState]}
                </StatusBadge>
              </TD>
              <TD>
                <div className="flex gap-space-1">
                  {ALL_STATES.filter((st) => st !== s.activationState).map((st) => (
                    <Button
                      key={st}
                      variant="text"
                      size="sm"
                      onClick={() => {
                        setError(null);
                        setReason("");
                        setPending({ series: s, next: st });
                      }}
                      data-testid={`catalog-flip-${s.code}-${st}`}
                    >
                      {STATE_LABEL[st]}
                    </Button>
                  ))}
                </div>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      {pending && (
        <Modal
          open
          onClose={() => setPending(null)}
          title={`Set ${pending.series.code} to ${STATE_LABEL[pending.next]}`}
          description={pending.series.title}
          footer={
            <>
              {error && (
                <p className="text-caption text-status-error m-0 mr-auto" role="alert">
                  {error}
                </p>
              )}
              <Button variant="secondary" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void confirm()}
                loading={update.isPending}
                data-testid="catalog-flip-confirm"
              >
                Confirm
              </Button>
            </>
          }
        >
          <label className="flex flex-col gap-space-1">
            <span className="text-small font-medium">Reason (optional)</span>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="catalog-flip-reason"
            />
          </label>
        </Modal>
      )}
    </Card>
  );
}
